import { create } from 'zustand';
import { fetchGameDbPlatforms, type GameDbPlatform, type GameDbProvider } from '../api/gamedb';
import { fetchSystems, fetchSystemVariants } from '../api/systems';
import type { SystemReference, VariantReference } from '../types';

const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

interface SystemStore {
  systems: SystemReference[];
  variants: VariantReference[];
  providers: GameDbProvider[];
  platforms: GameDbPlatform[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
  isFetching: boolean;
  fetchSystems: (getToken: () => Promise<string>) => Promise<void>;
  getSystemById: (id: string | number) => SystemReference | undefined;
  getVariantById: (id: string | number) => VariantReference | undefined;
  getProviderById: (id: string) => GameDbProvider | undefined;
  getPlatformById: (id: string) => GameDbPlatform | undefined;
  getPlatformByProviderAndPlatformId: (providerId: string, providerPlatformId: string) => GameDbPlatform | undefined;
}

export const useSystemStore = create<SystemStore>()((set, get) => ({
  systems: [],
  variants: [],
  providers: [],
  platforms: [],
  isLoading: false,
  error: null,
  lastFetched: null,
  isFetching: false,

  fetchSystems: async (getToken: () => Promise<string>) => {
    const now = Date.now();
    const { lastFetched, isFetching } = get();

    // If we have cached data and it's not expired, don't fetch
    if (lastFetched && now - lastFetched < CACHE_DURATION) {
      return;
    }

    // If there's already a request in progress, don't start another one
    if (isFetching) {
      return;
    }

    set({ isLoading: true, error: null, isFetching: true });

    try {
      const [systems, variants, platformsResponse] = await Promise.all([
        fetchSystems(getToken),
        fetchSystemVariants(getToken),
        fetchGameDbPlatforms(getToken),
      ]);

      // Flatten platforms from all providers
      const allPlatforms = platformsResponse.providers.flatMap((provider) => provider.platforms);

      set({
        systems,
        variants,
        providers: platformsResponse.providers,
        platforms: allPlatforms,
        isLoading: false,
        lastFetched: now,
        isFetching: false,
      });
    } catch (_error) {
      set({ error: 'Failed to fetch systems', isLoading: false, isFetching: false });
    }
  },

  getSystemById: (id: string | number) => {
    const idStr = String(id);
    return get().systems.find((system: SystemReference) => String(system.id) === idStr);
  },

  getVariantById: (id: string | number) => {
    const idStr = String(id);
    return get().variants.find((variant: VariantReference) => String(variant.id) === idStr);
  },

  getProviderById: (id: string) => {
    return get().providers.find((provider: GameDbProvider) => provider.id === id);
  },

  getPlatformById: (id: string) => {
    return get().platforms.find((platform: GameDbPlatform) => platform.id === id);
  },

  getPlatformByProviderAndPlatformId: (providerId: string, providerPlatformId: string) => {
    return get().platforms.find(
      (platform: GameDbPlatform) =>
        platform.providerId === providerId && platform.providerPlatformId === providerPlatformId,
    );
  },
}));
